import { subYears } from "date-fns";
import React, { useCallback, useMemo, useRef, useState } from "react";

// components
import { ButtonStandard, Pagination, SortChangedEvent, Table, TableRef } from "@performyard/py-components";
import ComponentLoader from "@performyard/py-components/src/components/atoms/ComponentLoader";

import ColumnPicker, { type DataSet, type DataValue } from "../../components/ColumnPicker";
import PageHeading from "../../components/PageHeading";
// utils
import { getCompanyFeedbackColumns } from "./components/CompanyFeedbackCols";
import CompanyFeedbackFilters, { type CompanyFeedbackFilter } from "./components/CompanyFeedbackFilters";
import { DownloadCompanyFeedbackCsvButton } from "./components/DownloadCompanyFeedbackCsvButton";
import ReadableCompanyFeedbackFilterChips from "./components/ReadableCompanyFeedbackFilterChips";

// hooks
import {
	useFeedbackStream,
	useRbacPrimaryRolePermissions,
	useSearchService,
	useSearchServicePrefetch,
} from "../../api/hooks";
import { useIsNewNavigation } from "../../api/hooks/defs/navigation";
import { useFeatureFlagQuery } from "../../hooks/useFeatureFlag";

import { SERVICE_FEEDBACK_COMPANY } from "../../api/services/defs/search-service";

import { Feedback } from "../UserFeedback/utils";
import {
	getFiltersForSearch,
	getFiltersForStream,
	isReferenceTypeCustomField,
	normalizeCompanyFeedbackFilters,
} from "./utils";

import InfoPageTemplate from "../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
//pageTemplate
import useInfoPageTemplateQueryState from "../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import ErrorPage from "../Error";

const sideNavigationItems = [{ key: "ALL_FEEDBACK", label: "Company Feedback", href: "/company/feedback" }];

const LIMIT = 10;
const DEFAULT_DATE_AFTER = subYears(new Date(), 1);

const CompanyFeedback = () => {
	const { data: permissions, isLoading: isRbacLoading } = useRbacPrimaryRolePermissions("feedback");
	const canViewCompanyFeedback = permissions?.includes("feedback:viewCompanyFeedback");
	const isNewNav = useIsNewNavigation();
	const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
	const { value: feedbackFeatureVisible } = useFeatureFlagQuery("ff-is-feedback-enabled", true);
	const { value: streamingFeedbackFeatureVisible } = useFeatureFlagQuery("ff-temp-streaming-company-feedback", false);

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		hasExplicitFilters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<CompanyFeedbackFilter>({
		defaultSortName: "last_updated",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
		normalizeFilters: normalizeCompanyFeedbackFilters,
		// For streaming, we want to limit default after date to DEFAULT_DATE_AFTER
		...(streamingFeedbackFeatureVisible && {
			fallbackFilters: { dateAfter: DEFAULT_DATE_AFTER },
		}),
	});

	const [enabledCustomFieldIds, setEnabledCustomFieldIds] = useState<string[]>([]);
	const [enabledCustomFieldRefIds, setEnabledCustomFieldRefIds] = useState<string[]>([]);

	const { data: searchServiceData, isLoading: isSearchServiceLoading } = useSearchService<Feedback>({
		service: SERVICE_FEEDBACK_COMPANY,
		filters: getFiltersForSearch(debouncedSearchValue, filters, enabledCustomFieldRefIds),
		skip: (pageNumber - 1) * LIMIT,
		limit: LIMIT,
		sort,
		defaultErrorMessage: "There was an error getting Feedbacks",
		disabled: streamingFeedbackFeatureVisible, // disable this query if we're using the fastify stream
	});
	const streamFilters = useMemo(
		() => getFiltersForStream(debouncedSearchValue, enabledCustomFieldRefIds, filters),
		[debouncedSearchValue, enabledCustomFieldRefIds, filters]
	);

	const { data: feedbackStreamResult, isLoading: isFeedbackStreamLoading } = useFeedbackStream(
		streamFilters,
		undefined,
		{ enabled: streamingFeedbackFeatureVisible }
	);

	const isLoading = streamingFeedbackFeatureVisible ? isFeedbackStreamLoading : isSearchServiceLoading;

	// prefetch page before and 3 pages after
	useSearchServicePrefetch<Feedback>(
		{
			service: SERVICE_FEEDBACK_COMPANY,
			filters: getFiltersForSearch(debouncedSearchValue, filters, enabledCustomFieldRefIds),
			skip: (pageNumber - 2) * LIMIT,
			limit: LIMIT,
			sort,
			disabled: streamingFeedbackFeatureVisible, // disable this query if we're using the fastify stream
		},
		pageNumber <= 1 ? 0 : 1
	);

	useSearchServicePrefetch<Feedback>(
		{
			service: SERVICE_FEEDBACK_COMPANY,
			filters: getFiltersForSearch(debouncedSearchValue, filters, enabledCustomFieldRefIds),
			skip: pageNumber * LIMIT,
			limit: LIMIT,
			sort,
			disabled: streamingFeedbackFeatureVisible, // disable this query if we're using the fastify stream
		},
		3
	);

	let feedbacks: any[] = [];
	let total = 0;
	let customFields: any[] = [];
	if (streamingFeedbackFeatureVisible) {
		feedbacks = feedbackStreamResult?.feedbacks || [];
		customFields = feedbackStreamResult?.customFields || [];
		total = feedbacks.length; // we don't have total count with streaming, so just use the length of the data we have
	} else {
		feedbacks = searchServiceData?.results || [];
		total = searchServiceData?.total || 0;
		customFields = searchServiceData?.customFields || [];
	}
	const customFieldRefIds = customFields.filter((cf: any) => isReferenceTypeCustomField(cf)).map((cf: any) => cf._id);

	const updateEnabledCustomFields = useCallback(
		(ids: string[]) => {
			setEnabledCustomFieldIds(ids);
			const refIds = ids.filter((id) => customFieldRefIds.includes(id));
			// Only update ref IDs when a new ref field is added — removing a column
			// doesn't require a re-fetch since the data is already resolved.
			const newId = refIds.find((id) => !enabledCustomFieldIds.includes(id));
			if (newId) setEnabledCustomFieldRefIds(refIds);
		},
		[enabledCustomFieldIds, customFieldRefIds]
	);

	const customFieldDataSets = useMemo((): DataSet[] => {
		if (customFields.length === 0) return [];
		return [
			{
				id: "custom-fields",
				label: "Subject Custom Fields",
				values: customFields.map((cf: any) => ({
					id: cf._id,
					label: cf.name,
					sectionID: "custom-fields",
				})),
			},
		];
	}, [customFields]);

	const handleToggleColumn = useCallback(
		(value: DataValue) => {
			const current = enabledCustomFieldIds.includes(value.id)
				? enabledCustomFieldIds.filter((id) => id !== value.id)
				: [...enabledCustomFieldIds, value.id];
			updateEnabledCustomFields(current);
		},
		[enabledCustomFieldIds, updateEnabledCustomFields]
	);

	const handleToggleAllColumns = useCallback(
		(allValues: DataValue[], allChecked: boolean) => {
			// allChecked was the previous value before toggle, so if it's true now that means we're unchecking all, otherwise we're checking all
			updateEnabledCustomFields(allChecked ? [] : allValues.map((v) => v.id));
		},
		[updateEnabledCustomFields]
	);

	const columnDefs = useMemo(
		() =>
			getCompanyFeedbackColumns({
				customFields,
				enabledCustomFieldIds,
				streaming: streamingFeedbackFeatureVisible,
			}),
		[customFields, enabledCustomFieldIds, streamingFeedbackFeatureVisible]
	);

	const onSortChanged = (event: SortChangedEvent<Feedback>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			setSort({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	const gridRef = useRef<TableRef<Feedback>>(null);

	const handleExportCsv = useCallback(() => {
		gridRef.current?.api.exportDataAsCsv();
	}, []);

	if (isRbacLoading) {
		return <ComponentLoader />;
	}

	if (!canViewCompanyFeedback) {
		return <ErrorPage code={403} />;
	}

	if (!feedbackFeatureVisible) {
		return <ErrorPage code={404} />;
	}

	const mainContent = (
		<div>
			<div className={`flex ${isNewNav ? "h-[calc(100vh-260px)]" : "h-[calc(100vh-220px)]"}`}>
				<div className="min-w-0 flex-1">
					<Table<Feedback>
						ref={gridRef}
						rowData={feedbacks}
						columnDefs={columnDefs}
						loading={isLoading}
						getRowId={(params) => params.data._id}
						onSortChanged={onSortChanged}
						suppressScrollOnNewData={true} // Don't scroll when data is changed
						emptyStateText="No Feedback Found"
						initialState={{
							sort: {
								sortModel: sort.name
									? [{ colId: sort.name, sort: sort.direction as "asc" | "desc" }]
									: [],
							},
						}}
						animateRows={false}
					/>
				</div>
				{customFieldDataSets.length > 0 && (
					<div className="h-full w-[280px] shrink-0 overflow-y-auto border-l border-neutral-2">
						<ColumnPicker
							dataSets={customFieldDataSets}
							checkedItems={enabledCustomFieldIds}
							onToggleItem={handleToggleColumn}
							onToggleAllItems={handleToggleAllColumns}
							initialExpandedSections={["custom-fields"]}
						/>
					</div>
				)}
			</div>
			{!streamingFeedbackFeatureVisible && total > 0 && (
				<div className="mx-auto mt-5 flex w-full max-w-[400px] justify-center">
					<Pagination
						currentPage={pageNumber}
						pageCount={Math.ceil(total / LIMIT)}
						onPageChange={(newPage: number) => setPageNumber(newPage)}
					/>
				</div>
			)}
		</div>
	);

	const filterDrawer = (
		<CompanyFeedbackFilters
			isOpen={isFilterDrawerOpen}
			onClose={() => setIsFilterDrawerOpen(false)}
			filters={filters}
			onChange={setFilters}
		/>
	);

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			searchPlaceholder="Search Feedback..."
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			onFilterButtonPress={() => setIsFilterDrawerOpen(true)}
			activeFiltersCount={hasExplicitFilters ? Object.keys(filters).length : 0}
		/>
	);

	const actionButtons = streamingFeedbackFeatureVisible
		? [
				<ButtonStandard key="download" variant="outline" onClick={handleExportCsv}>
					Download CSV
				</ButtonStandard>,
			]
		: [<DownloadCompanyFeedbackCsvButton key="download" />];

	return (
		<InfoPageTemplate
			mainContent={mainContent}
			pageTitle="Company Feedback"
			pageHeading={isNewNav ? <PageHeading>Company Feedback</PageHeading> : undefined}
			navigationLinks={sideNavigationItems}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={<ReadableCompanyFeedbackFilterChips filters={filters} setFilters={setFilters} />}
			drawer={filterDrawer}
			actionButtons={actionButtons}
			activeNavigationLinkKey={"ALL_FEEDBACK"}
		/>
	);
};

export default CompanyFeedback;
